import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import {
  fetchBillingContext,
  fetchOrgUsage,
  fetchPlanCatalog,
  fetchUnattachedSubscriptions,
  readCurrentPlan,
  type CurrentPlan,
  type McpPeriod,
} from "@/lib/api/organizations";
import { billingKeys } from "./keys";

/// Read on mount and when asked, nothing more: a retry would double a request
/// against a rate-limited billing door, and an offline read fails, not pauses.
const READ_WHEN_ASKED = {
  retry: false,
  staleTime: 0,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  networkMode: "always",
} as const;

/// `failed` answers the first read and outlives later ones; `plan` is `null`
/// for an org with no recorded period.
export interface PlanRead {
  plan: CurrentPlan | null;
  failed: boolean;
}

const planReadQuery = (orgId: string) =>
  queryOptions({
    queryKey: billingKeys.plan(orgId),
    queryFn: async ({ client, queryKey }): Promise<PlanRead> => {
      const read = await readCurrentPlan(orgId);
      const shown = client.getQueryData<PlanRead>(queryKey);
      if (!shown) return { plan: read ?? null, failed: read === undefined };
      // A re-read only ever brings a plan in; throwing keeps the one shown.
      if (!read) throw new Error("The plan re-read found no period.");
      return { ...shown, plan: read };
    },
    ...READ_WHEN_ASKED,
  });

export function usePlanRead(orgId: string) {
  return useQuery(planReadQuery(orgId));
}

/// Throwing on a failed read keeps the context already shown.
export function useBillingContextQuery(orgId: string) {
  return useQuery({
    queryKey: billingKeys.context(orgId),
    queryFn: async () => {
      const context = await fetchBillingContext(orgId);
      if (!context) throw new Error("The billing context could not be read.");
      return context;
    },
    ...READ_WHEN_ASKED,
  });
}

export function useOrgUsage(orgId: string) {
  return useQuery({
    queryKey: billingKeys.usage(orgId),
    queryFn: () => fetchOrgUsage(orgId),
    ...READ_WHEN_ASKED,
  });
}

/// The MCP breakdown, fetched only once someone opens it.
export function useMcpStats(
  orgId: string,
  period: McpPeriod,
  enabled: boolean,
) {
  return useQuery({
    queryKey: billingKeys.mcpStats(orgId, period),
    queryFn: async () =>
      (await fetchOrgUsage(orgId, { mcpBreakdown: period }))?.mcp_breakdown ??
      null,
    enabled,
    // Switching periods keeps the chart up, so the page does not collapse and jump.
    placeholderData: keepPreviousData,
    ...READ_WHEN_ASKED,
  });
}

export function usePlanCatalog() {
  return useQuery({
    queryKey: billingKeys.catalog(),
    queryFn: () => fetchPlanCatalog(),
    ...READ_WHEN_ASKED,
  });
}

export function useUnattachedSubscriptions(orgId: string) {
  return useQuery({
    queryKey: billingKeys.unattached(orgId),
    queryFn: () => fetchUnattachedSubscriptions(orgId),
    ...READ_WHEN_ASKED,
  });
}
